/*
 * Reading one file of the offline routing graph (scripts/routing/FORMAT.md):
 * a gzip of a 32-byte header and little-endian typed arrays.
 *
 * Pure: bytes in, arrays out. Decompression is passed in (`inflate`) so the
 * tests can use Node's zlib; in the browser it is DecompressionStream.
 */
export const MAGIC = 'PRT1';
export const FORMAT_VERSION = 1;
export const KIND_DETAIL = 0;
export const KIND_TRUNK = 1;
const HEADER_BYTES = 32;

// Flag bits (scripts/routing/profiles.py).
export const CAR2_FWD = 1;
export const CAR2_BWD = 2;
export const CAR4_FWD = 4;
export const CAR4_BWD = 8;
export const FOOT = 16;
export const TRAIL = 32;
export const NODE_CAR_BLOCKED = 1;

const LITTLE = new Uint8Array(new Uint32Array([1]).buffer)[0] === 1;

/** The decompressed bytes of a gzip, in the browser. */
export async function inflateGzip(bytes) {
  if (typeof DecompressionStream === 'undefined') throw new Error('DecompressionStream is not available');
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}

function reader(buffer) {
  const view = new DataView(buffer);
  let at = HEADER_BYTES;
  const typed = (Ctor, count, size, getter) => {
    const bytes = count * size;
    if (at + bytes > buffer.byteLength) throw new Error('a routing tile is cut short');
    let out;
    if (LITTLE || size === 1) {
      out = new Ctor(buffer.slice(at, at + bytes));
    } else {
      out = new Ctor(count);
      for (let i = 0; i < count; i += 1) out[i] = view[getter](at + i * size, true);
    }
    at += bytes;
    return out;
  };
  return {
    u32: (n) => typed(Uint32Array, n, 4, 'getUint32'),
    i32: (n) => typed(Int32Array, n, 4, 'getInt32'),
    u8: (n) => typed(Uint8Array, n, 1, 'getUint8'),
    end: () => at,
  };
}

/** The arrays of one decompressed file (an ArrayBuffer). */
export function decodeTile(buffer) {
  if (!(buffer instanceof ArrayBuffer)) buffer = buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
  if (buffer.byteLength < HEADER_BYTES) throw new Error('not a routing tile');
  const view = new DataView(buffer);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  const version = view.getUint16(4, true);
  if (magic !== MAGIC || version !== FORMAT_VERSION) throw new Error('not a routing tile of this version');
  const kind = view.getUint16(6, true);
  const row = view.getInt32(8, true);
  const col = view.getInt32(12, true);
  const n = view.getUint32(16, true);
  const m = view.getUint32(20, true);
  const s = view.getUint32(24, true);
  const r = reader(buffer);
  const tile = { kind, row, col };
  tile.nodeId = r.u32(n);
  tile.nodeLat = r.i32(n);
  tile.nodeLon = r.i32(n);
  tile.edgeId = r.u32(m);
  tile.edgeA = r.u32(m);
  tile.edgeB = r.u32(m);
  tile.edgeLen = r.u32(m);
  tile.shapeOff = r.u32(m + 1);
  tile.shapeDlat = r.i32(s);
  tile.shapeDlon = r.i32(s);
  tile.nodeFlags = r.u8(n);
  tile.edgeFlags = r.u8(m);
  tile.edgeCls = r.u8(m);
  tile.edgeSurf = r.u8(m);
  tile.edgeGrade = r.u8(m);
  tile.edgeSac = r.u8(m);
  if (r.end() !== buffer.byteLength) throw new Error('trailing bytes in a routing tile');
  return tile;
}

/** Decompress, then decode. */
export async function readTile(bytes, inflate = inflateGzip) {
  return decodeTile(await inflate(bytes));
}
